import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question:
      "Does a depression diagnosis in my early twenties mean my life is over?",
    answer:
      "No. It means you have a treatable medical condition, you caught it early, and you have more options than any generation before you. The late teens and early twenties are when depression most often appears for the first time — three-quarters of people who will ever have an anxiety or mood disorder show symptoms before 22. Most people who get treated recover, and many describe the diagnosis as the moment things started getting better.",
  },
  {
    question: "What does 'major depressive disorder' actually mean?",
    answer:
      "MDD means at least two weeks of persistent low mood or loss of interest, plus several other symptoms: sleep and appetite changes, fatigue, trouble concentrating, feelings of worthlessness, sometimes thoughts of death. It's a description of what you're experiencing, not a prediction of your future. It doesn't mean you can't finish school, hold a job, or have relationships.",
  },
  {
    question: "What if my first medication doesn't work?",
    answer:
      "If you've given a medication a real trial and you're still depressed, tell your doctor. If you've tried two and you're still depressed, that has a name: treatment-resistant depression. It's not rare, and it's not the end. The goal of treatment is remission — symptoms gone, not reduced. Don't settle for 'a little better' if the number isn't where it should be.",
  },
  {
    question: "What is SAINT and does it fit a young adult's life?",
    answer:
      "SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared, accelerated form of TMS developed at Stanford's Brain Stimulation Lab. It's cleared for adults 18 and older with major depressive disorder who haven't gotten adequate relief from antidepressants. It uses a 45-minute functional MRI of your own brain to identify the precise target, then delivers ten 10-minute stimulation sessions a day, with 50-minute breaks, for five consecutive days. No anesthesia, nothing in your bloodstream, and it fits inside a break.",
  },
  {
    question: "Do I have to stop my current medications for SAINT?",
    answer:
      "No. Most patients continue their existing medication regimen through SAINT treatment. Nothing enters the bloodstream, so there's no interaction with existing medications. SAINT isn't a replacement for what you've built — it's a different kind of intervention aimed at the brain circuitry your medications may not have reached. Never change or stop medication without guidance from your prescribing provider.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      "Depression in College: Why the 'Best Years of Your Life' Can Be the Hardest",
    href: "/depression-in-college",
    description:
      "Why depression often starts in college, warning signs for students and parents, and what actually works.",
  },
  {
    title:
      "You Just Started Therapy at 27. That's Not Late. That's the Beginning.",
    href: "/you-just-started-therapy-at-27-thats-not-late",
    description:
      "Why starting treatment later than you'd hoped is still starting, and what to expect in the first weeks.",
  },
  {
    title:
      "Diagnosed With Depression at 25: What Your Twenties Can Still Look Like",
    href: "/diagnosed-with-depression-at-25",
    description:
      "Why 25 is a common age for diagnosis, what to expect from treatment, and what your twenties can still look like.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function YoungAdultDiagnosedDepression() {
  return (
    <DepressionContentPage
      seo={{
        title: "Diagnosed With Depression as a Young Adult? It's the Beginning",
        description:
          "A depression diagnosis between 18 and 24 is not the end of your story. What it means, why now, what treatment looks like, and the option most young adults never hear about.",
        canonical: "/diagnosed-with-depression-as-a-young-adult",
        keywords:
          "young adult depression diagnosis, major depressive disorder early twenties, treatment-resistant depression young adult, SAINT TMS college student, depression first episode, campus counseling, remission not improvement, fMRI-guided TMS",
      }}
      badge="For Young Adults"
      heroTitle="You're a Young Adult Who Was Just Diagnosed With Depression. This Is the Beginning, Not the End."
      heroSubtitle="You're somewhere between 18 and 24, and in the middle of the years everyone says are supposed to be the best of your life, a doctor just told you that you have major depressive disorder. Here's the honest answer about what that means for the rest of your life."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                You're somewhere between 18 and 24. College, maybe, or a first
                job, or figuring out which. And in the middle of the years
                everyone says are supposed to be the best of your life, a doctor
                just told you that you have major depressive disorder.
              </p>
              <p>
                If you're feeling a strange mix of relief and dread, that's
                normal. The relief is because the thing finally has a name. The
                dread is because you're wondering what the name means for the
                rest of your life.
              </p>
              <p>
                Here's the honest answer: it means you have a treatable medical
                condition, you caught it early, and you have more options than
                any generation before you.
              </p>
            </div>
          ),
        },
        {
          heading: "Why now",
          body: (
            <div className="space-y-5">
              <p>
                The late teens and early twenties are when depression most often
                appears for the first time. Three-quarters of people who will
                ever have an anxiety or mood disorder show symptoms before 22.
                That isn't because something's wrong with you. It's because this
                is the age when the brain finishes developing, when structure
                and routine fall away, when sleep and eating go sideways, and
                when genetic vulnerability gets its first real test.
              </p>
              <p>
                If depression runs in your family, that's the vulnerability. The
                circumstances of young adulthood are what triggered it. You
                didn't cause it and you didn't fail to prevent it.
              </p>
            </div>
          ),
        },
        {
          heading: "What the diagnosis actually says",
          body: (
            <div className="space-y-5">
              <p>
                MDD means at least two weeks of persistent low mood or loss of
                interest, plus several other symptoms: sleep and appetite
                changes, fatigue, trouble concentrating, feelings of
                worthlessness, sometimes thoughts of death. It's a description
                of what you're experiencing, not a prediction of your future.
              </p>
              <p>
                Some things it doesn't mean: that you can't finish school, that
                you can't hold a job, that relationships are off the table, or
                that you'll feel this way forever. Most people who get treated
                recover. Many go on to describe the diagnosis as the moment
                things started getting better.
              </p>
            </div>
          ),
        },
        {
          heading: "What treatment looks like",
          body: (
            <div className="space-y-5">
              <p>
                Start with therapy, often cognitive behavioral therapy. If your
                symptoms are moderate to severe, a doctor may add an
                antidepressant. The combination is more effective than either
                alone. Campus counseling centers are a good entry point if
                you're in school; they can refer you to providers in the
                community.
              </p>
              <p>
                Take care of the basics, which matter more than you'd think:
                sleep on a schedule, eat real food, move daily, and cut back on
                alcohol and drugs, which reliably deepen depression.
              </p>
              <p>
                For most young adults, this works. Give it real time; medication
                takes six to eight weeks to show its effect.
              </p>
            </div>
          ),
        },
        {
          heading: 'Don\'t settle for "a little better"',
          body: (
            <div className="space-y-5">
              <p>
                Here's the thing people don't tell you. Some people get partial
                relief from medication and accept it. They function, they get
                through the semester, they stop mentioning it. And they spend
                years at 60 percent because nobody told them the goal was
                remission, not improvement.
              </p>
              <p>
                If you've given a medication a real trial and you're still
                depressed, tell your doctor. If you've tried two and you're
                still depressed, that has a name: treatment-resistant
                depression. It's not rare, and it's not the end.
              </p>
            </div>
          ),
        },
        {
          heading: "The option most young adults never hear about",
          body: (
            <div className="space-y-4">
              <p>
                Depression isn't only a chemistry problem. It's also a circuitry
                problem. The region behind your forehead that regulates mood,
                motivation, and focus is underactive, and medication doesn't
                directly retrain it.
              </p>
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an FDA-cleared, accelerated form of transcranial magnetic
                stimulation developed at Stanford's Brain Stimulation Lab. It's
                cleared for adults 18 and older with major depressive disorder
                who haven't gotten adequate relief from antidepressants.
              </p>
              <p>
                It uses a 45-minute functional MRI of your own brain to identify
                the precise target, then delivers ten 10-minute stimulation
                sessions a day, with 50-minute breaks, for five consecutive
                days. No anesthesia. Nothing enters your bloodstream. Five days,
                which fits inside a break.
              </p>
              <p>
                Here's how it works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation.</strong> Our clinical team reviews your
                  history, your therapy and medication trials, and confirms
                  you're a candidate. The initial consultation is a Zoom call;
                  as steps evolve, you'll also have the option to meet with our
                  psychiatrist by Zoom web conference or in person. The
                  mandatory in-person steps include fMRI mapping and the actual
                  treatment.
                </li>
                <li>
                  <strong>fMRI brain scan.</strong> A roughly 45-minute
                  functional MRI maps how your individual brain networks are
                  wired.
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location for
                  stimulation. Neuronavigation guides the coil there for every
                  session.
                </li>
                <li>
                  <strong>Five-day stimulation series.</strong> Ten 10-minute
                  sessions per day, with 50-minute breaks in between, for five
                  consecutive days. Five clinic visits instead of 30 to 36. It
                  fits inside a break.
                </li>
              </ol>

              <SaintComparisonStatBand />

              <p>
                In clinical trials of people who had already failed medication,
                79 percent reached remission, with an average time to remission
                of 2.6 days. Across more than 400 trial patients, 85 percent
                experienced symptom relief, with no serious adverse events. Side
                effects were mostly mild headache, fatigue, and scalp
                discomfort. Your current medications don't need to be stopped.
              </p>
              <p>
                You may never need it. But knowing it exists changes what
                "treatment-resistant" means. It doesn't mean stuck. It means you
                haven't reached the right treatment yet.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                Follow through on the plan you were given. Give it a real
                chance. Track your symptoms so you and your doctor can see
                whether it's working. And if it isn't, don't wait years to ask
                what's next.
              </p>
              <p>
                At Neuro Wellness TMS Centers of America, a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>
                , the first appointment is an evaluation, not a commitment. We
                offer private outpatient care with weekend availability, and
                many young adults are still on a parent's insurance. We'll tell
                you honestly what fits.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you are in crisis or thinking about harming yourself, call or
                text 988 (Suicide &amp; Crisis Lifeline) or go to your nearest
                emergency room. This article is for educational purposes and is
                not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
